<template>
  <div class="global-audio-player">
    <audio ref="audio" preload="metadata" @loadedmetadata="onLoadedMetadata" @timeupdate="onTimeUpdate" @play="onPlay" @pause="onPause" @waiting="onWaiting" @canplay="onCanPlay" @ended="onEnded" @error="onError"></audio>
    <aside v-if="showMini" class="audio-mini" aria-label="正在播放的有声书">
      <button class="mini-cover" type="button" :aria-label="`打开《${player.book.title}》播放器`" @click="openPlayer">
        <protected-cover v-if="player.book.coverImage && !coverBroken" :src="player.book.coverImage" alt="" fit="contain" @error="coverBroken = true" />
        <nricon v-else name="headphones" :size="19" />
      </button>
      <button class="mini-title" type="button" @click="openPlayer"><strong>{{ player.book.title }}</strong><span>{{ activeTrack && activeTrack.title }}</span></button>
      <div class="mini-progress" aria-label="播放进度"><i :style="{ width: progressPercent + '%' }"></i></div>
      <button class="mini-control" type="button" :aria-label="player.isPlaying ? '暂停' : '播放'" @click="togglePlay"><nricon :name="player.isPlaying ? 'video-pause' : 'video-play'" :size="21" /></button>
      <button class="mini-control next-control" type="button" aria-label="下一轨" @click="nextTrack"><nricon name="arrow-right" :size="18" /></button>
      <button class="mini-control close-control" type="button" aria-label="关闭播放器" @click="closePlayer"><nricon name="close" :size="17" /></button>
    </aside>
  </div>
</template>

<script>
import api from '@/api'
import ProtectedCover from '@/components/common/ProtectedCover.vue'

export default {
  name: 'GlobalAudioPlayer',
  components: { ProtectedCover },
  data() { return { _loadedTrackId: null, _restoreTime: null, _autoplayAfterLoad: false, _tokenIssuedAt: 0, _refreshingToken: false, _saveInterval: null, _sleepTimeout: null, _statsTimer: null, _statsStartedAt: 0, _statsBookId: null, _lastPersistedAt: 0, coverBroken: false } },
  computed: {
    player() { return this.$store.state.audioPlayer },
    activeTrack() { return this.player.tracks[this.player.trackIndex] || null },
    showMini() { return !!(this.player.book && this.activeTrack && !this.$route.meta.noShell && this.$route.name !== 'Listen') },
    progressPercent() {
      const duration = Number(this.player.duration) || Number(this.activeTrack && this.activeTrack.durationSeconds) || 0
      return duration > 0 ? Math.max(0, Math.min(100, (this.player.currentTime / duration) * 100)) : 0
    }
  },
  watch: {
    activeTrack(track) {
      if (!track) { this.stopElement(); return }
      if (Number(track.id) !== Number(this._loadedTrackId)) {
        this.prepareTrack(this._autoplayAfterLoad)
      }
      this.persistSession(true)
    },
    '$store.state.token'(token) { if (!token && this.player.book) this.closePlayer() },
    '$store.state.audioPlayer.book'(book) {
      this.coverBroken = false
      if (book) this.persistSession(true)
      else localStorage.removeItem('nr-audio-session')
    },
    '$store.state.audioPlayer.playbackRate'(rate) { if (this.$refs.audio) this.$refs.audio.playbackRate = Number(rate) || 1 },
    '$store.state.audioPlayer.currentTime'(value) {
      if (!this.$refs.audio || !this._loadedTrackId || this._refreshingToken || this.player.isPlaying) return
      if (Math.abs((Number(this.$refs.audio.currentTime) || 0) - (Number(value) || 0)) > 2) this.seek(value)
    }
  },
  mounted() {
    if (this.activeTrack) this.prepareTrack(false)
    else if (this.$route.name !== 'Listen') this.restoreSession()
    this._saveInterval = setInterval(this.saveProgress, 15000)
    this._statsTimer = setInterval(() => this.flushAudioStats(true), 30000)
    window.addEventListener('nr:audio-pause', this.pauseAudio)
    window.addEventListener('nr:audio-seek', this.onExternalSeek)
    window.addEventListener('nr:audio-sleep', this.onExternalSleep)
    window.addEventListener('nr:audio-toggle', this.togglePlay)
    window.addEventListener('nr:audio-next', this.nextTrack)
    window.addEventListener('nr:audio-previous', this.previousTrack)
    window.addEventListener('nr:audio-select', this.onExternalSelect)
  },
  beforeDestroy() {
    clearInterval(this._saveInterval)
    clearInterval(this._statsTimer)
    clearTimeout(this._sleepTimeout)
    this.flushAudioStats(false)
    window.removeEventListener('nr:audio-pause', this.pauseAudio)
    window.removeEventListener('nr:audio-seek', this.onExternalSeek)
    window.removeEventListener('nr:audio-sleep', this.onExternalSleep)
    window.removeEventListener('nr:audio-toggle', this.togglePlay)
    window.removeEventListener('nr:audio-next', this.nextTrack)
    window.removeEventListener('nr:audio-previous', this.previousTrack)
    window.removeEventListener('nr:audio-select', this.onExternalSelect)
      this.saveProgress()
      this.persistSession(true)
      this.stopElement()
  },
  methods: {
    async prepareTrack(autoplay, seekTo) {
      const track = this.activeTrack
      if (!track || this._refreshingToken) return
      const audio = this.$refs.audio
      if (!audio) return
      this._refreshingToken = true
      this._autoplayAfterLoad = !!autoplay
      this._restoreTime = seekTo == null ? Number(this.player.currentTime) || 0 : Number(seekTo) || 0
      this.$store.commit('updateAudioPlayer', { buffering: true, error: '', isPlaying: false })
      audio.pause()
      try {
        const { data } = await api.book.getAudioStreamAccess(this.player.book.id, track.id)
        this._tokenIssuedAt = Date.now()
        this._loadedTrackId = track.id
        audio.src = api.book.getAudioStreamUrl(track.id, data.token)
        audio.playbackRate = Number(this.player.playbackRate) || 1
        audio.load()
      } catch (error) {
        if (error.response && error.response.status === 403) {
          this.$message.error('此有声书已无法访问，播放器已关闭')
          this.closePlayer()
        } else {
          this.$store.commit('updateAudioPlayer', { buffering: false, error: '音频载入失败，请重试' })
        }
        this._refreshingToken = false
      }
    },
    onLoadedMetadata() {
      const audio = this.$refs.audio
      if (!audio) return
      this.$store.commit('updateAudioPlayer', { duration: Number.isFinite(audio.duration) ? audio.duration : Number(this.activeTrack && this.activeTrack.durationSeconds) || 0 })
      if (this._restoreTime != null && this._restoreTime > 0) {
        try { audio.currentTime = Math.min(this._restoreTime, Math.max(0, audio.duration - 1)) } catch (error) { /* metadata may not be seekable yet */ }
      }
      this._restoreTime = null
      this._refreshingToken = false
      this.$store.commit('updateAudioPlayer', { buffering: false })
      if (this._autoplayAfterLoad) {
        this._autoplayAfterLoad = false
        this.playElement()
      }
    },
    async playElement() {
      const audio = this.$refs.audio
      if (!audio || !audio.src) return this.prepareTrack(true)
      try {
        window.dispatchEvent(new CustomEvent('nr:audio-play'))
        await audio.play()
      } catch (error) {
        this.$store.commit('updateAudioPlayer', { isPlaying: false, error: '浏览器阻止了自动播放，请再按一次播放' })
      }
    },
    togglePlay() {
      if (this.player.isPlaying) this.pauseAudio()
      else if (this._loadedTrackId !== (this.activeTrack && this.activeTrack.id) || !this.$refs.audio.src) this.prepareTrack(true)
      else this.playElement()
    },
    pauseAudio() {
      this.flushAudioStats(false)
      if (this.$refs.audio) this.$refs.audio.pause()
      this.$store.commit('updateAudioPlayer', { isPlaying: false })
    },
    onExternalSeek(event) {
      const seconds = Number(event.detail) || 0
      if (this._refreshingToken || !this.$refs.audio || this.$refs.audio.readyState < 1) this._restoreTime = seconds
      else this.seek(seconds)
    },
    onExternalSleep(event) { this.setSleepTimer(Number(event.detail) || 0) },
    async onExternalSelect(event) {
      const index = Number(event.detail)
      if (!Number.isInteger(index) || index < 0 || index >= this.player.tracks.length) return
      if (index === this.player.trackIndex) return this.togglePlay()
      await this.saveProgress()
      const track = this.player.tracks[index]
      this._autoplayAfterLoad = true
      this.$store.commit('setAudioSession', { ...this.player, trackIndex: index, currentTime: 0, duration: Number(track.durationSeconds) || 0 })
    },
    stopElement() {
      const audio = this.$refs.audio
      if (!audio) return
      audio.pause(); audio.removeAttribute('src'); audio.load()
      this._loadedTrackId = null
    },
    onPlay() {
      this.$store.commit('updateAudioPlayer', { isPlaying: true, error: '' })
      if (this.$store.state.token && !this._statsStartedAt) {
        this._statsStartedAt = Date.now()
        this._statsBookId = this.player.book && this.player.book.id
      }
    },
    onPause() {
      this.flushAudioStats(false)
      this.$store.commit('updateAudioPlayer', { isPlaying: false })
      this.persistSession(true)
    },
    onWaiting() { this.$store.commit('updateAudioPlayer', { buffering: true }) },
    onCanPlay() { this.$store.commit('updateAudioPlayer', { buffering: false }) },
    onTimeUpdate() {
      const audio = this.$refs.audio
      if (!audio) return
      this.$store.commit('updateAudioPlayer', { currentTime: Number(audio.currentTime) || 0 })
      this.persistSession(false)
      if (Date.now() - this._tokenIssuedAt > 12 * 60 * 1000 && this.player.isPlaying && !this._refreshingToken) {
        const position = audio.currentTime
        this.prepareTrack(true, position)
      }
    },
    onError() {
      if (!this.activeTrack || this._refreshingToken) return
      if (Date.now() - this._tokenIssuedAt > 12 * 60 * 1000) this.prepareTrack(this.player.isPlaying, this.player.currentTime)
      else this.$store.commit('updateAudioPlayer', { error: '音频播放失败，请检查文件格式或网络连接' })
    },
    async onEnded() {
      const completed = this.player.trackIndex === this.player.tracks.length - 1
      await this.saveProgress(completed)
      if (this.player.sleepMinutes === -1) {
        this.$store.commit('updateAudioPlayer', { isPlaying: false, sleepMinutes: 0 })
        return
      }
      if (this.player.trackIndex < this.player.tracks.length - 1) this.nextTrack()
      else this.$store.commit('updateAudioPlayer', { isPlaying: false, currentTime: 0 })
    },
    async nextTrack() {
      if (!this.player.book || this.player.trackIndex >= this.player.tracks.length - 1) return
      await this.saveProgress()
      const index = this.player.trackIndex + 1
      const track = this.player.tracks[index]
      this._autoplayAfterLoad = true
      this.$store.commit('setAudioSession', { ...this.player, trackIndex: index, currentTime: 0, duration: Number(track.durationSeconds) || 0 })
    },
    async previousTrack() {
      if (this.$refs.audio && this.$refs.audio.currentTime > 3) { this.$refs.audio.currentTime = 0; return }
      if (!this.player.book || this.player.trackIndex <= 0) { if (this.$refs.audio) this.$refs.audio.currentTime = 0; return }
      await this.saveProgress()
      const index = this.player.trackIndex - 1
      const track = this.player.tracks[index]
      this._autoplayAfterLoad = true
      this.$store.commit('setAudioSession', { ...this.player, trackIndex: index, currentTime: 0, duration: Number(track.durationSeconds) || 0 })
    },
    seek(seconds) { if (this.$refs.audio && Number.isFinite(this.$refs.audio.duration)) this.$refs.audio.currentTime = Math.max(0, Math.min(this.$refs.audio.duration, Number(seconds) || 0)) },
    setPlaybackRate(rate) {
      const value = Math.max(0.5, Math.min(3, Number(rate) || 1))
      if (this.$refs.audio) this.$refs.audio.playbackRate = value
      this.$store.commit('updateAudioPlayer', { playbackRate: value })
    },
    setSleepTimer(minutes) {
      clearTimeout(this._sleepTimeout)
      const value = Number(minutes) || 0
      this.$store.commit('updateAudioPlayer', { sleepMinutes: value })
      if (value > 0) this._sleepTimeout = setTimeout(() => { this.pauseAudio(); this.$store.commit('updateAudioPlayer', { sleepMinutes: 0 }) }, value * 60 * 1000)
    },
    async saveProgress(completed = false) {
      const track = this.activeTrack
      if (!track || !this.player.book || !this.$store.state.token) return
      const audio = this.$refs.audio
      const position = audio ? Number(audio.currentTime) || 0 : Number(this.player.currentTime) || 0
      if (position <= 0 && !completed) return
      const nativeDuration = Number(audio && audio.duration)
      const savedTrackDuration = Number(track.durationSeconds)
      const trackDuration = Number.isFinite(nativeDuration) && nativeDuration > 0
        ? nativeDuration : Number.isFinite(savedTrackDuration) && savedTrackDuration > 0 ? savedTrackDuration : null
      const totalDuration = Number(this.player.book.totalDurationSeconds)
      try {
        await api.progress.saveReadingProgress({
          bookId: this.player.book.id, mediaType: 'AUDIO', chapterId: null,
          trackId: track.id, positionSeconds: position,
          currentTrackDurationSeconds: trackDuration,
          totalDurationSeconds: Number.isFinite(totalDuration) && totalDuration > 0 ? totalDuration : null,
          overallPositionSeconds: this.overallPosition(position),
          completed: !!completed,
          trackIndex: this.player.trackIndex, totalTracks: this.player.tracks.length,
          trackTitle: track.title,
          readingMode: 'audio'
        })
      } catch (error) { /* network loss must not interrupt playback */ }
    },
    overallPosition(position) {
      let total = 0
      for (let index = 0; index < this.player.trackIndex; index++) {
        const duration = Number(this.player.tracks[index].durationSeconds)
        if (!Number.isFinite(duration) || duration <= 0) return null
        total += duration
      }
      return total + Math.max(0, Number(position) || 0)
    },
    flushAudioStats(continuePlaying) {
      if (!this._statsStartedAt) return
      const now = Date.now()
      const seconds = Math.floor((now - this._statsStartedAt) / 1000)
      const shouldContinue = !!(continuePlaying && this.player.isPlaying && this.$refs.audio && !this.$refs.audio.paused)
      this._statsStartedAt = shouldContinue ? now : 0
      if (seconds > 0 && this.$store.state.token && this._statsBookId) {
        api.stats.heartbeat({ bookId: Number(this._statsBookId), seconds, mediaType: 'AUDIO' }).catch(() => {})
      }
      if (!shouldContinue) this._statsBookId = null
    },
    persistSession(force) {
      const now = Date.now()
      if (!force && now - this._lastPersistedAt < 10000) return
      this._lastPersistedAt = now
      if (!this.player.book || !this.activeTrack) return
      const audioIsCurrent = String(this._loadedTrackId) === String(this.activeTrack.id)
      const position = audioIsCurrent && this.$refs.audio && Number.isFinite(this.$refs.audio.currentTime)
        ? Number(this.$refs.audio.currentTime) : Number(this.player.currentTime) || 0
      localStorage.setItem('nr-audio-session', JSON.stringify({
        bookId: this.player.book.id,
        trackId: this.activeTrack.id,
        currentTime: position,
        userId: this.$store.state.user && this.$store.state.user.id
      }))
    },
    async restoreSession() {
      let saved
      try { saved = JSON.parse(localStorage.getItem('nr-audio-session') || 'null') } catch (error) { saved = null }
      if (!saved || !Number(saved.bookId)) return
      const currentUserId = this.$store.state.user && this.$store.state.user.id
      if ((saved.userId != null && String(saved.userId) !== String(currentUserId)) || (!this.$store.state.token && saved.userId != null)) {
        localStorage.removeItem('nr-audio-session')
        return
      }
      try {
        await this.$store.dispatch('loadAudioBook', Number(saved.bookId))
        const trackIndex = this.player.tracks.findIndex(track => String(track.id) === String(saved.trackId))
        const index = trackIndex >= 0 ? trackIndex : this.player.trackIndex
        const track = this.player.tracks[index]
        this.$store.commit('setAudioSession', {
          ...this.player,
          trackIndex: index,
          currentTime: Math.max(0, Number(saved.currentTime) || 0),
          duration: Number(track && track.durationSeconds) || 0
        })
        this.persistSession(true)
      } catch (error) {
        if (error.response && [401, 403, 404].includes(error.response.status)) localStorage.removeItem('nr-audio-session')
      }
    },
    async openPlayer() { this.$router.push({ name: 'Listen', params: { bookId: String(this.player.book.id) } }) },
    async closePlayer() {
      await this.saveProgress()
      this.flushAudioStats(false)
      this.pauseAudio()
      this.stopElement()
      this.$store.commit('clearAudioPlayer')
      localStorage.removeItem('nr-audio-session')
      clearTimeout(this._sleepTimeout)
      window.dispatchEvent(new CustomEvent('nr:audio-stop'))
    }
  }
}
</script>

<style lang="scss" scoped>
.global-audio-player audio { display: none; }
.audio-mini { position: fixed; right: 20px; bottom: 16px; left: 236px; z-index: 110; display: flex; height: 66px; align-items: center; gap: 10px; padding: 8px 12px; border: 1px solid var(--nr-border); border-radius: 13px; background: color-mix(in srgb, var(--nr-surface) 94%, transparent); box-shadow: var(--nr-shadow-float); backdrop-filter: blur(20px); }
.mini-cover { display: grid; width: 46px; height: 48px; flex: 0 0 46px; place-items: center; overflow: hidden; color: var(--nr-accent-text); border: 0; border-radius: 7px; background: var(--nr-accent-bg); cursor: pointer; img { width: 100%; height: 100%; object-fit: cover; } }
.mini-title { display: flex; min-width: 0; flex: 1; flex-direction: column; gap: 5px; padding: 0 5px; color: var(--nr-text); border: 0; background: transparent; text-align: left; cursor: pointer; strong,.mini-title span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; } strong { font-size: 12px; font-weight: 650; } span { color: var(--nr-text-3); font-size: 10px; } }
.mini-progress { position: absolute; right: 164px; bottom: 0; left: 69px; height: 2px; overflow: hidden; border-radius: 2px; background: var(--nr-surface-3); i { display: block; height: 100%; background: var(--nr-accent); transition: width .15s linear; } }
.mini-control { display: grid; width: 36px; height: 36px; flex: 0 0 36px; place-items: center; color: var(--nr-text-2); border: 0; border-radius: 50%; background: transparent; cursor: pointer; &:hover { color: var(--nr-accent); background: var(--nr-accent-bg); } }
.close-control { color: var(--nr-text-3); }
@media (min-width: 768px) and (max-width: 1199px) { .audio-mini { left: 100px; } }
@media (max-width: 767px) { .audio-mini { right: 8px; bottom: calc(68px + env(safe-area-inset-bottom)); left: 8px; height: 58px; gap: 6px; padding: 5px 7px; border-radius: 11px; } .mini-cover { width: 42px; height: 44px; flex-basis: 42px; } .mini-control { width: 32px; height: 32px; flex-basis: 32px; } .mini-progress { right: 112px; bottom: 0; left: 56px; } .close-control { display: none; } }
</style>
